import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, ChevronDown, LogIn, Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/brand/logo'
import { ServiceIcon } from '@/components/site/service-icon'
import { LanguageSwitch } from '@/components/site/language-switch'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { useLocale } from '@/i18n/use-locale'
import { common } from '@/i18n/common'
import { serviceSummaries } from '@/content/services-index'

interface HeaderProps {
    tone?: 'light' | 'dark'
}

type Panel = 'services' | 'resources' | null

export const Header: FC<HeaderProps> = ({ tone = 'light' }): ReactNode => {
    const router = useRouter()
    const locale = useLocale()
    const copy = common[locale].nav
    const [scrolled, setScrolled] = useState(false)
    const [panel, setPanel] = useState<Panel>(null)
    const [mobileOpen, setMobileOpen] = useState(false)
    const headerRef = useRef<HTMLElement>(null)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => {
            setPanel(null)
            setMobileOpen(false)
        }

        router.events.on('routeChangeStart', close)
        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!panel) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setPanel(null)
        }

        const onClick = (event: MouseEvent) => {
            if (headerRef.current && !headerRef.current.contains(event.target as Node)) setPanel(null)
        }

        document.addEventListener('keydown', onKey)
        document.addEventListener('mousedown', onClick)

        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('mousedown', onClick)
        }
    }, [panel])

    const dark = tone === 'dark' && !scrolled && !panel
    const practice = serviceSummaries.filter(item => item.practice)
    const featured = serviceSummaries.filter(item => !item.practice)

    const linkClass = cn(
        'rounded-full px-3 py-2 text-[0.92rem] font-medium transition-colors',
        dark ? 'text-white/80 hover:text-white' : 'text-foreground/75 hover:text-foreground'
    )

    const isActive = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <header
            ref={headerRef}
            className={cn(
                'fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-300',
                dark ? 'border-b border-transparent bg-transparent' : 'border-b border-border/70 bg-background/90 shadow-[0_1px_0_0_rgba(0,0,0,0.02)] backdrop-blur-xl'
            )}
        >
            <a href='#main' className='sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm'>
                {common[locale].skipToContent}
            </a>

            <div className='container-page flex h-[72px] items-center justify-between gap-6'>
                <Link href='/' className='rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring' aria-label='Ura Advisory'>
                    <Logo tone={dark ? 'light' : 'brand'} />
                </Link>

                <nav className='hidden items-center gap-0.5 lg:flex' aria-label='Main'>
                    <button
                        type='button'
                        className={cn(linkClass, 'inline-flex items-center gap-1', isActive('/services') && !dark && 'text-foreground')}
                        aria-expanded={panel === 'services'}
                        aria-controls='menu-services'
                        onClick={() => setPanel(panel === 'services' ? null : 'services')}
                    >
                        {copy.services}
                        <ChevronDown className={cn('size-4 transition-transform', panel === 'services' && 'rotate-180')} aria-hidden='true' />
                    </button>

                    <Link href='/industries' className={cn(linkClass, isActive('/industries') && !dark && 'text-foreground')}>
                        {copy.industries}
                    </Link>

                    <Link href='/ai-solutions' className={cn(linkClass, isActive('/ai-solutions') && !dark && 'text-foreground')}>
                        {copy.aiSolutions}
                    </Link>

                    <button
                        type='button'
                        className={cn(linkClass, 'inline-flex items-center gap-1')}
                        aria-expanded={panel === 'resources'}
                        aria-controls='menu-resources'
                        onClick={() => setPanel(panel === 'resources' ? null : 'resources')}
                    >
                        {copy.resources}
                        <ChevronDown className={cn('size-4 transition-transform', panel === 'resources' && 'rotate-180')} aria-hidden='true' />
                    </button>

                    <Link href='/about' className={cn(linkClass, isActive('/about') && !dark && 'text-foreground')}>
                        {copy.about}
                    </Link>

                    <Link href='/pricing' className={cn(linkClass, isActive('/pricing') && !dark && 'text-foreground')}>
                        {copy.pricing}
                    </Link>

                    <Link href='/client-portal' className={cn(linkClass, isActive('/client-portal') && !dark && 'text-foreground')}>
                        {copy.clientPortal}
                    </Link>
                </nav>

                <div className='hidden items-center gap-2 lg:flex'>
                    <LanguageSwitch tone={dark ? 'dark' : 'light'} />

                    <Link
                        href='/portal/login'
                        className={cn('inline-flex h-10 items-center gap-1.5 rounded-full px-3.5 text-sm font-medium transition-colors', dark ? 'text-white/85 hover:text-white' : 'text-foreground/80 hover:text-foreground')}
                    >
                        <LogIn className='size-4' aria-hidden='true' />
                        {copy.clientLogin}
                    </Link>

                    <Link
                        href='/contact'
                        className={cn('inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-sm font-medium transition-colors', dark ? 'bg-white text-ink hover:bg-white/90' : 'bg-ink text-white hover:bg-ink-soft')}
                    >
                        {copy.bookConsultation}
                    </Link>
                </div>

                <button
                    type='button'
                    className={cn('inline-flex size-10 items-center justify-center rounded-full lg:hidden', dark ? 'text-white' : 'text-foreground')}
                    onClick={() => setMobileOpen(true)}
                    aria-label={copy.openMenu}
                >
                    <Menu className='size-6' aria-hidden='true' />
                </button>
            </div>

            {panel === 'services' && (
                <div id='menu-services' className='absolute inset-x-0 top-full border-b border-border bg-background shadow-xl shadow-ink/5 ura-reveal'>
                    <div className='container-page grid gap-8 py-8 lg:grid-cols-[1fr_320px]'>
                        <div>
                            <p className='mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                {copy.practiceAreas}
                            </p>

                            <div className='grid gap-1 sm:grid-cols-2 xl:grid-cols-3'>
                                {practice.map(item => (
                                    <Link
                                        key={item.slug}
                                        href={`/services/${item.slug}`}
                                        className='group flex gap-3 rounded-xl p-3 transition-colors hover:bg-mist'
                                    >
                                        <span className='mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-primary'>
                                            <ServiceIcon name={item.icon} className='size-5' />
                                        </span>

                                        <span>
                                            <span className='block text-sm font-semibold text-foreground'>
                                                {item.name[locale]}
                                            </span>

                                            <span className='mt-0.5 line-clamp-2 block text-[0.8rem] leading-snug text-muted-foreground'>
                                                {item.summary[locale]}
                                            </span>
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div className='flex flex-col gap-3 rounded-2xl bg-ink p-5 text-white'>
                            <p className='text-xs font-semibold uppercase tracking-[0.18em] text-white/55'>
                                {copy.featured}
                            </p>

                            {featured.map(item => (
                                <Link key={item.slug} href={`/services/${item.slug}`} className='group rounded-xl border border-white/10 p-3 transition-colors hover:border-white/25 hover:bg-white/5'>
                                    <span className='flex items-center gap-2 text-sm font-semibold'>
                                        <ServiceIcon name={item.icon} className='size-4 text-signal' />
                                        {item.name[locale]}
                                    </span>

                                    <span className='mt-1 line-clamp-2 block text-[0.8rem] leading-snug text-white/65'>
                                        {item.summary[locale]}
                                    </span>
                                </Link>
                            ))}

                            <Link href='/services' className='mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-signal hover:text-white'>
                                {copy.allServices}
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {panel === 'resources' && (
                <div id='menu-resources' className='absolute inset-x-0 top-full border-b border-border bg-background shadow-xl shadow-ink/5 ura-reveal'>
                    <div className='container-page grid gap-3 py-6 sm:grid-cols-3'>
                        {[
                            { href: '/insights', title: copy.insights, text: copy.insightsDescription },
                            { href: '/blog', title: copy.blog, text: copy.blogDescription },
                            { href: '/case-studies', title: copy.caseStudies, text: copy.caseStudiesDescription }
                        ].map(item => (
                            <Link key={item.href} href={item.href} className='group rounded-xl border border-border p-4 transition-colors hover:border-primary/30 hover:bg-mist'>
                                <span className='flex items-center justify-between text-sm font-semibold'>
                                    {item.title}
                                    <ArrowRight className='size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary' aria-hidden='true' />
                                </span>

                                <span className='mt-1 block text-[0.82rem] text-muted-foreground'>
                                    {item.text}
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetContent side='right' className='w-full max-w-sm overflow-y-auto p-0'>
                    <SheetTitle className='sr-only'>
                        {copy.openMenu}
                    </SheetTitle>

                    <div className='flex items-center justify-between border-b border-border px-5 py-4'>
                        <Logo />
                    </div>

                    <nav className='flex flex-col gap-1 p-4' aria-label='Mobile'>
                        <p className='px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground'>
                            {copy.services}
                        </p>

                        {serviceSummaries.map(item => (
                            <Link key={item.slug} href={`/services/${item.slug}`} className='flex items-center gap-3 rounded-lg px-2 py-2.5 text-[0.95rem] font-medium hover:bg-mist'>
                                <ServiceIcon name={item.icon} className='size-4 text-primary' />
                                {item.name[locale]}
                            </Link>
                        ))}

                        <div className='my-2 h-px bg-border' />

                        {[
                            { href: '/industries', label: copy.industries },
                            { href: '/ai-solutions', label: copy.aiSolutions },
                            { href: '/insights', label: copy.insights },
                            { href: '/blog', label: copy.blog },
                            { href: '/case-studies', label: copy.caseStudies },
                            { href: '/about', label: copy.about },
                            { href: '/pricing', label: copy.pricing },
                            { href: '/client-portal', label: copy.clientPortal },
                            { href: '/contact', label: copy.contact }
                        ].map(item => (
                            <Link key={item.href} href={item.href} className='rounded-lg px-2 py-2.5 text-[0.95rem] font-medium hover:bg-mist'>
                                {item.label}
                            </Link>
                        ))}

                        <div className='mt-4 flex flex-col gap-2'>
                            <Link href='/contact' className='inline-flex h-11 items-center justify-center rounded-full bg-ink px-4 text-sm font-medium text-white'>
                                {copy.bookConsultation}
                            </Link>

                            <Link href='/portal/login' className='inline-flex h-11 items-center justify-center gap-1.5 rounded-full border border-border px-4 text-sm font-medium'>
                                <LogIn className='size-4' aria-hidden='true' />
                                {copy.clientLogin}
                            </Link>
                        </div>

                        <LanguageSwitch className='mt-4 self-start' />
                    </nav>
                </SheetContent>
            </Sheet>
        </header>
    )
}
